import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Play, Clock, BookOpen, Award, Download } from 'lucide-react';
import '../styles/Pages.css';

const Tutorials = () => {
  const tutorials = [
    {
      title: 'Primeros pasos con VillaTracking',
      duration: '15 min',
      level: 'Principiante',
      description: 'Aprende a configurar tu cuenta y comenzar a utilizar las funciones básicas de la plataforma.',
      thumbnail: 'https://images.pexels.com/photos/7516363/pexels-photo-7516363.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      title: 'Configuración de geocercas',
      duration: '12 min',
      level: 'Intermedio',
      description: 'Aprende a crear y configurar geocercas para monitorear entradas y salidas de áreas definidas.',
      thumbnail: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      title: 'Generación de reportes avanzados',
      duration: '20 min',
      level: 'Avanzado',
      description: 'Descubre cómo crear reportes personalizados para analizar el rendimiento de tu flota.',
      thumbnail: 'https://images.pexels.com/photos/7516339/pexels-photo-7516339.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      title: 'Configuración de alertas personalizadas',
      duration: '10 min',
      level: 'Intermedio',
      description: 'Aprende a configurar notificaciones para eventos específicos como excesos de velocidad o paradas prolongadas.',
      thumbnail: 'https://images.pexels.com/photos/3861458/pexels-photo-3861458.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      title: 'Uso de la aplicación móvil',
      duration: '18 min',
      level: 'Principiante',
      description: 'Guía completa sobre cómo utilizar todas las funciones de la aplicación móvil de VillaTracking.',
      thumbnail: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      title: 'Gestión de mantenimiento preventivo',
      duration: '25 min',
      level: 'Avanzado',
      description: 'Aprende a configurar y gestionar programas de mantenimiento basados en kilometraje o tiempo.',
      thumbnail: 'https://images.pexels.com/photos/3807319/pexels-photo-3807319.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    }
  ];

  const guides = [
    {
      title: 'Guía de instalación de dispositivos GPS',
      pages: 15,
      format: 'PDF',
      description: 'Instrucciones detalladas para la instalación correcta de dispositivos GPS en diferentes tipos de vehículos.'
    },
    {
      title: 'Manual de usuario de la plataforma web',
      pages: 42,
      format: 'PDF',
      description: 'Documentación completa sobre todas las funcionalidades de la plataforma web de VillaTracking.'
    },
    {
      title: 'Guía de referencia rápida',
      pages: 8,
      format: 'PDF',
      description: 'Resumen de las funciones más utilizadas para consulta rápida.'
    },
    {
      title: 'Manual de integración API',
      pages: 35,
      format: 'PDF',
      description: 'Documentación técnica para desarrolladores que desean integrar VillaTracking con otros sistemas.'
    }
  ];

  return (
    <div className="page-container">
      <Header scrolled={true} />
      
      <main className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1>Tutoriales y Guías</h1>
            <p>Recursos para aprovechar al máximo tu sistema VillaTracking</p>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <h2>Videotutoriales</h2>
            <p className="section-description">
              Aprende a utilizar todas las funcionalidades de VillaTracking con nuestros videotutoriales paso a paso.
            </p>
            
            <div className="tutorials-grid">
              {tutorials.map((tutorial, index) => (
                <div className="tutorial-card" key={index}>
                  <div className="tutorial-thumbnail">
                    <img src={tutorial.thumbnail} alt={tutorial.title} />
                    <div className="play-button">
                      <Play size={24} />
                    </div>
                    <div className="tutorial-duration">
                      <Clock size={14} />
                      <span>{tutorial.duration}</span>
                    </div>
                  </div>
                  <div className="tutorial-content">
                    <div className="tutorial-level">
                      <Award size={14} />
                      <span>{tutorial.level}</span>
                    </div>
                    <h3>{tutorial.title}</h3>
                    <p>{tutorial.description}</p>
                    <a href={`/tutorials/video/${index + 1}`} className="tutorial-link">Ver Tutorial</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="page-section page-section-alt">
          <div className="container">
            <h2>Guías y Manuales</h2>
            <p className="section-description">
              Documentación detallada para consulta y referencia.
            </p>
            
            <div className="guides-list">
              {guides.map((guide, index) => (
                <div className="guide-card" key={index}>
                  <div className="guide-icon">
                    <BookOpen size={24} />
                  </div>
                  <div className="guide-content">
                    <h3>{guide.title}</h3>
                    <p>{guide.description}</p>
                    <div className="guide-meta">
                      <span className="guide-pages">{guide.pages} páginas</span>
                      <span className="guide-format">{guide.format}</span>
                    </div>
                  </div>
                  <a href={`/downloads/guides/${guide.title.toLowerCase().replace(/ /g, '-')}.pdf`} className="guide-download">
                    <Download size={20} />
                    <span>Descargar</span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <div className="webinars-section">
              <h2>Webinars y Capacitaciones</h2>
              <p className="section-description">
                Sesiones de formación en vivo y grabadas para profundizar en funcionalidades específicas.
              </p>
              
              <div className="webinars-upcoming">
                <h3>Próximos Webinars</h3>
                <div className="webinar-card upcoming">
                  <div className="webinar-date">
                    <div className="date-day">15</div>
                    <div className="date-month">Dic</div>
                  </div>
                  <div className="webinar-content">
                    <h4>Optimización de Rutas y Reducción de Costos Operativos</h4>
                    <p>Aprende a utilizar las herramientas de análisis y optimización para reducir costos de operación.</p>
                    <div className="webinar-time">
                      <Clock size={14} />
                      <span>10:00 AM (GMT-4)</span>
                    </div>
                  </div>
                  <a href="/webinars/register/1" className="btn btn-primary">Registrarse</a>
                </div>
                
                <div className="webinar-card upcoming">
                  <div className="webinar-date">
                    <div className="date-day">22</div>
                    <div className="date-month">Dic</div>
                  </div>
                  <div className="webinar-content">
                    <h4>Novedades y Funcionalidades de la Versión 3.0</h4>
                    <p>Descubre todas las nuevas características y mejoras de la última versión de VillaTracking.</p>
                    <div className="webinar-time">
                      <Clock size={14} />
                      <span>15:00 PM (GMT-4)</span>
                    </div>
                  </div>
                  <a href="/webinars/register/2" className="btn btn-primary">Registrarse</a>
                </div>
              </div>
              
              <div className="webinars-recorded">
                <h3>Webinars Grabados</h3>
                <div className="recorded-webinars-grid">
                  <div className="recorded-webinar">
                    <div className="recorded-thumbnail">
                      <img src="https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Webinar Grabado" />
                      <div className="play-button">
                        <Play size={24} />
                      </div>
                    </div>
                    <h4>Mejores Prácticas de Seguridad Vehicular</h4>
                    <div className="recorded-meta">
                      <span>45 min</span>
                      <span>Nov 10, 2023</span>
                    </div>
                  </div>
                  
                  <div className="recorded-webinar">
                    <div className="recorded-thumbnail">
                      <img src="https://images.pexels.com/photos/8867431/pexels-photo-8867431.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Webinar Grabado" />
                      <div className="play-button">
                        <Play size={24} />
                      </div>
                    </div>
                    <h4>Análisis Avanzado de Datos de Flota</h4>
                    <div className="recorded-meta">
                      <span>60 min</span>
                      <span>Oct 25, 2023</span>
                    </div>
                  </div>
                  
                  <div className="recorded-webinar">
                    <div className="recorded-thumbnail">
                      <img src="https://images.pexels.com/photos/8867265/pexels-photo-8867265.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Webinar Grabado" />
                      <div className="play-button">
                        <Play size={24} />
                      </div>
                    </div>
                    <h4>Integración con Sistemas de Gestión Empresarial</h4>
                    <div className="recorded-meta">
                      <span>55 min</span>
                      <span>Oct 5, 2023</span>
                    </div>
                  </div>
                </div>
                
                <div className="view-all-webinars">
                  <a href="/webinars/recorded" className="btn btn-secondary">Ver Todos los Webinars</a>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="page-cta">
          <div className="container">
            <h2>¿Necesitas capacitación personalizada?</h2>
            <p>Ofrecemos sesiones de formación adaptadas a las necesidades específicas de tu empresa</p>
            <a href="mailto:soporte@villatracking.com" className="btn btn-primary btn-lg">Solicitar Capacitación</a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Tutorials;